import * as React from 'react';
import { useEffect, useLayoutEffect as useLayoutEffect$1, useRef, createContext, useContext, useMemo, useCallback, useState, memo, forwardRef, useImperativeHandle } from 'react';
import clsx from 'clsx';
import isHotKey from 'is-hotkey';
import useOnclickOutside from 'react-cool-onclickoutside'; // eslint-disable-next-line no-restricted-imports
// Silence silly warning
// https://reactjs.org/link/uselayouteffect-ssr

const useLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect$1;

function useFocusRef(isSelected) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    var _a;

    if (!isSelected) return;
    (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus({
      preventScroll: true
    });
  }, [isSelected]);
  return {
    ref,
    tabIndex: isSelected ? 0 : -1
  };
}

const checkboxLabelClassname = `rdt-checkbox-label`;
const checkboxInputClassname = `rdt-checkbox-input`;
const checkboxClassname = `rdt-checkbox`;
const checkboxLabelDisabledClassname = `rdt-checkbox-label-disabled`;

function SelectCellFormatter({
  value,
  isCellSelected,
  disabled,
  onClick,
  onChange,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy
}) {
  const {
    ref,
    tabIndex
  } = useFocusRef(isCellSelected);

  function handleChange(e) {
    onChange(e.target.checked, e.nativeEvent.shiftKey);
  }

  return /*#__PURE__*/React.createElement("label", {
    className: clsx(checkboxLabelClassname, {
      [checkboxLabelDisabledClassname]: disabled
    })
  }, /*#__PURE__*/React.createElement("input", {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    ref: ref,
    type: "checkbox",
    tabIndex: tabIndex,
    className: checkboxInputClassname,
    disabled: disabled,
    checked: value,
    onChange: handleChange,
    onClick: onClick
  }), /*#__PURE__*/React.createElement("div", {
    className: checkboxClassname
  }));
}

function ValueFormatter(props) {
  try {
    return /*#__PURE__*/React.createElement(React.Fragment, null, props.row[props.column.key]);
  } catch (_a) {
    return null;
  }
}

const RowSelectionContext = /*#__PURE__*/createContext(undefined);
const RowSelectionProvider = RowSelectionContext.Provider;
const RowSelectionChangeContext = /*#__PURE__*/createContext(undefined);
const RowSelectionChangeProvider = RowSelectionChangeContext.Provider;

function useRowSelection() {
  const rowSelectionContext = useContext(RowSelectionContext);
  const rowSelectionChangeContext = useContext(RowSelectionChangeContext);

  if (rowSelectionContext === undefined || rowSelectionChangeContext === undefined) {
    throw new Error('useRowSelection must be used within DataGrid cells');
  }

  return [rowSelectionContext, rowSelectionChangeContext];
}

const cellClassname = `rdt-cell`;
const cellFrozenClassname = `rdt-cell-frozen`;
const cellFrozenLastClassname = `rdt-cell-frozen-last`;
const rootClassname = `rdt`;
const viewportDraggingClassname = `rdt-viewport-dragging cursor-move`;
const rowClassname = `rdt-row`;

function getColSpan(column, lastFrozenColumnIndex, args) {
  const colSpan = typeof column.colSpan === 'function' ? column.colSpan(args) : 1;

  if (Number.isInteger(colSpan) && colSpan > 1 && ( // ignore colSpan if it spans over both frozen and regular columns
  !column.frozen || column.idx + colSpan - 1 <= lastFrozenColumnIndex)) {
    return colSpan;
  }

  return undefined;
}

function stopPropagation(event) {
  event.stopPropagation();
} // https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values


const nonInputKeys = new Set([// Special keys
'Unidentified', // Modifier keys
'Alt', 'AltGraph', 'CapsLock', 'Control', 'Fn', 'FnLock', 'Meta', 'NumLock', 'ScrollLock', 'Shift', // Whitespace keys
'Tab', // Navigation keys
'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp', // Editing
'Insert', // UI keys
'ContextMenu', 'Escape', 'Pause', 'Play', // Device keys
'PrintScreen', // Function keys
'F1', // 'F2', /!\ specifically allowed, do not edit
'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12']);

function isCtrlKeyHeldDown(e) {
  return (e.ctrlKey || e.metaKey) && e.key !== 'Control';
}

function isDefaultCellInput(event) {
  return !nonInputKeys.has(event.key);
}
/**
 * By default, the following navigation keys are enabled while an editor is open, under specific conditions:
 * - Tab:
 *   - The editor must be an <input>, a <textarea>, or a <select> element.
 *   - The editor element must be the only immediate child of the editor container/a label.
 */


function onEditorNavigation({
  key,
  target
}) {
  if (key === 'Tab' && (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) {
    return target.matches('.rdt-editor-container > :only-child, .rdt-editor-container > label:only-child > :only-child');
  }

  return false;
}

function isSelectedCellEditable({
  selectedPosition,
  columns,
  rows
}) {
  const column = columns[selectedPosition.idx];
  const row = rows[selectedPosition.rowIdx];
  return isCellEditable(column, row);
}

function isCellEditable(column, row) {
  return column.editor != null && (typeof column.editable === 'function' ? column.editable(row) : column.editable) !== false;
}

function getSelectedCellColSpan({
  rows,
  summaryRows,
  rowIdx,
  lastFrozenColumnIndex,
  column
}) {
  if (rowIdx === -1) {
    return getColSpan(column, lastFrozenColumnIndex, {
      type: 'HEADER'
    });
  }

  if (rowIdx >= 0 && rowIdx < rows.length) {
    const row = rows[rowIdx];
    return getColSpan(column, lastFrozenColumnIndex, {
      type: 'ROW',
      row
    });
  }

  if (summaryRows) {
    return getColSpan(column, lastFrozenColumnIndex, {
      type: 'SUMMARY',
      row: summaryRows[rowIdx - rows.length]
    });
  }

  return undefined;
}

function getNextSelectedCellPosition({
  cellNavigationMode,
  columns,
  colSpanColumns,
  rows,
  summaryRows,
  minRowIdx,
  maxRowIdx,
  currentPosition: {
    idx: currentIdx
  },
  nextPosition,
  lastFrozenColumnIndex,
  isCellWithinBounds
}) {
  let {
    idx: nextIdx,
    rowIdx: nextRowIdx
  } = nextPosition;

  const setColSpan = moveRight => {
    // If a cell within the colspan range is selected then move to the
    // previous or the next cell depending on the navigation direction
    for (const column of colSpanColumns) {
      const colIdx = column.idx;
      if (colIdx > nextIdx) break;
      const colSpan = getSelectedCellColSpan({
        rows,
        summaryRows,
        rowIdx: nextRowIdx,
        lastFrozenColumnIndex,
        column
      });

      if (colSpan && nextIdx > colIdx && nextIdx < colSpan + colIdx) {
        nextIdx = colIdx + (moveRight ? colSpan : 0);
        break;
      }
    }
  };

  if (isCellWithinBounds(nextPosition)) {
    setColSpan(nextIdx - currentIdx > 0);
  }

  if (cellNavigationMode !== 'NONE') {
    const columnsCount = columns.length;
    const isAfterLastColumn = nextIdx === columnsCount;
    const isBeforeFirstColumn = nextIdx === -1;

    if (isAfterLastColumn) {
      if (cellNavigationMode === 'CHANGE_ROW') {
        const isLastRow = nextRowIdx === maxRowIdx;

        if (!isLastRow) {
          nextIdx = 0;
          nextRowIdx += 1;
        }
      } else {
        nextIdx = 0;
      }
    } else if (isBeforeFirstColumn) {
      if (cellNavigationMode === 'CHANGE_ROW') {
        const isFirstRow = nextRowIdx === minRowIdx;

        if (!isFirstRow) {
          nextRowIdx -= 1;
          nextIdx = columnsCount - 1;
        }
      } else {
        nextIdx = columnsCount - 1;
      }

      setColSpan(false);
    }
  }

  return {
    idx: nextIdx,
    rowIdx: nextRowIdx
  };
}

function canExitGrid({
  cellNavigationMode,
  maxColIdx,
  minRowIdx,
  maxRowIdx,
  selectedPosition: {
    rowIdx,
    idx
  },
  shiftKey
}) {
  // When the cellNavigationMode is 'none' or 'changeRow', you can exit the grid if you're at the first or last cell of the grid
  // When the cellNavigationMode is 'loopOverRow', there is no logical exit point so you can't exit the grid
  if (cellNavigationMode === 'NONE' || cellNavigationMode === 'CHANGE_ROW') {
    const atLastCellInRow = idx === maxColIdx;
    const atFirstCellInRow = idx === 0;
    const atLastRow = rowIdx === maxRowIdx;
    const atFirstRow = rowIdx === minRowIdx;
    return shiftKey ? atFirstCellInRow && atFirstRow : atLastCellInRow && atLastRow;
  }

  return false;
}

function getSelectedRangeDimensions({
  selectedRange: {
    topLeft,
    bottomRight
  },
  tableElement
}) {
  if (topLeft.idx < 0) {
    return {
      width: 0,
      left: 0,
      top: 0,
      height: 0,
      zIndex: 1
    };
  }

  let width = 0;
  let top = 0;
  let height = 0;
  let left = 0;

  for (let i = 0; i <= bottomRight.idx; i++) {
    const elem = tableElement === null || tableElement === void 0 ? void 0 : tableElement.childNodes[0].childNodes[i];

    if (i < topLeft.idx) {
      left += (elem === null || elem === void 0 ? void 0 : elem.offsetWidth) || 0;
    } else {
      width += (elem === null || elem === void 0 ? void 0 : elem.offsetWidth) || 0;
    }
  }

  for (let i = 0; i <= bottomRight.rowIdx + 1; i++) {
    const elem = tableElement === null || tableElement === void 0 ? void 0 : tableElement.childNodes[i];

    if (i < topLeft.rowIdx + 1) {
      top += (elem === null || elem === void 0 ? void 0 : elem.offsetHeight) || 0;
    } else {
      height += (elem === null || elem === void 0 ? void 0 : elem.offsetHeight) || 0;
    }
  }

  return {
    width,
    left: left,
    top,
    height,
    zIndex: 0
  };
}

function selectedRangeIsSingleCell({
  topLeft,
  bottomRight
}) {
  return topLeft.idx === bottomRight.idx && topLeft.rowIdx === bottomRight.rowIdx;
}

const {
  min,
  max,
  floor,
  sign
} = Math;

function assertIsValidKeyGetter(keyGetter) {
  if (typeof keyGetter !== 'function') {
    throw new Error('Please specify the rowKeyGetter prop to use selection');
  }
}

function getCellStyle(column, colSpan) {
  return {
    gridColumnStart: column.idx + 1,
    gridColumnEnd: colSpan !== undefined ? `span ${colSpan}` : undefined,
    left: column.frozen ? `var(--frozen-left-${column.idx})` : undefined
  };
}

function getCellClassname(column, ...extraClasses) {
  return clsx(cellClassname, {
    [cellFrozenClassname]: column.frozen,
    [cellFrozenLastClassname]: column.isLastFrozenColumn
  }, ...extraClasses);
}

const SELECT_COLUMN_KEY = 'select-row';

function SelectFormatter(props) {
  const [isRowSelected, onRowSelectionChange] = useRowSelection();
  return /*#__PURE__*/React.createElement(SelectCellFormatter, {
    "aria-label": "Select",
    isCellSelected: props.isCellSelected,
    value: isRowSelected,
    onClick: stopPropagation,
    onChange: (checked, isShiftClick) => {
      onRowSelectionChange({
        row: props.row,
        checked,
        isShiftClick
      });
    }
  });
}

function SelectGroupFormatter(props) {
  const [isRowSelected, onRowSelectionChange] = useRowSelection();
  return /*#__PURE__*/React.createElement(SelectCellFormatter, {
    "aria-label": "Select Group",
    isCellSelected: props.isCellSelected,
    value: isRowSelected,
    onChange: checked => {
      onRowSelectionChange({
        row: props.row,
        checked,
        isShiftClick: false
      });
    },
    // Stop propagation to prevent row selection
    onClick: stopPropagation
  });
} // eslint-disable-next-line @typescript-eslint/no-explicit-any


const SelectColumn = {
  key: SELECT_COLUMN_KEY,
  name: '',
  width: 35,
  maxWidth: 35,
  resizable: false,
  sortable: false,
  frozen: true,

  headerRenderer(props) {
    return /*#__PURE__*/React.createElement(SelectCellFormatter, {
      "aria-label": "Select All",
      isCellSelected: props.isCellSelected,
      value: props.allRowsSelected,
      onChange: props.onAllRowsSelectionChange,
      // Stop propagation to prevent row selection
      onClick: stopPropagation
    });
  },

  formatter: SelectFormatter,
  groupFormatter: SelectGroupFormatter
};
/* eslint-disable react/react-in-jsx-scope */

const cellDragHandleClassname = `rdt-cell-drag-handle`;

function DragHandle({
  rows,
  columns,
  selectedPosition,
  latestDraggedOverRowIdx,
  isCellEditable,
  onRowsChange,
  onFill,
  setDragging,
  setDraggedOverRowIdx
}) {
  function handleMouseDown(event) {
    if (event.buttons !== 1) return;
    setDragging(true);
    window.addEventListener('mouseover', onMouseOver);
    window.addEventListener('mouseup', onMouseUp);

    function onMouseOver(event) {
      // Trigger onMouseup in edge cases where we release the mouse button but `mouseup` isn't triggered,
      // for example when releasing the mouse button outside the iframe the grid is rendered in.
      // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
      if (event.buttons !== 1) onMouseUp();
    }

    function onMouseUp() {
      window.removeEventListener('mouseover', onMouseOver);
      window.removeEventListener('mouseup', onMouseUp);
      setDragging(false);
      handleDragEnd();
    }
  }

  function handleDragEnd() {
    const overRowIdx = latestDraggedOverRowIdx.current;
    if (overRowIdx === undefined) return;
    const {
      rowIdx
    } = selectedPosition;
    const startRowIndex = rowIdx < overRowIdx ? rowIdx + 1 : overRowIdx;
    const endRowIndex = rowIdx < overRowIdx ? overRowIdx + 1 : rowIdx;
    updateRows(startRowIndex, endRowIndex);
    setDraggedOverRowIdx(undefined);
  }

  function handleDoubleClick(event) {
    event.stopPropagation();
    updateRows(selectedPosition.rowIdx + 1, rows.length);
  }

  function updateRows(startRowIdx, endRowIdx) {
    const {
      idx,
      rowIdx
    } = selectedPosition;
    const column = columns[idx];
    const sourceRow = rows[rowIdx];
    const updatedRows = [...rows];
    const indexes = [];

    for (let i = startRowIdx; i < endRowIdx; i++) {
      if (isCellEditable({
        rowIdx: i,
        idx
      })) {
        const updatedRow = onFill({
          columnKey: column.key,
          sourceRow,
          targetRow: rows[i]
        });

        if (updatedRow !== rows[i]) {
          updatedRows[i] = updatedRow;
          indexes.push(i);
        }
      }
    }

    if (indexes.length > 0) {
      onRowsChange === null || onRowsChange === void 0 ? void 0 : onRowsChange(updatedRows, {
        indexes,
        column
      });
    }
  }

  return /*#__PURE__*/React.createElement("div", {
    className: cellDragHandleClassname,
    onMouseDown: handleMouseDown,
    onDoubleClick: handleDoubleClick
  });
}

function useCalculatedColumns({
  rawColumns,
  columnWidths,
  viewportWidth,
  scrollLeft,
  defaultColumnOptions,
  enableVirtualization
}) {
  var _a, _b, _c, _d;

  const minColumnWidth = (_a = defaultColumnOptions === null || defaultColumnOptions === void 0 ? void 0 : defaultColumnOptions.minWidth) !== null && _a !== void 0 ? _a : 80;
  const defaultFormatter = (_b = defaultColumnOptions === null || defaultColumnOptions === void 0 ? void 0 : defaultColumnOptions.formatter) !== null && _b !== void 0 ? _b : ValueFormatter;
  const defaultSortable = (_c = defaultColumnOptions === null || defaultColumnOptions === void 0 ? void 0 : defaultColumnOptions.sortable) !== null && _c !== void 0 ? _c : false;
  const defaultResizable = (_d = defaultColumnOptions === null || defaultColumnOptions === void 0 ? void 0 : defaultColumnOptions.resizable) !== null && _d !== void 0 ? _d : false;
  const {
    columns,
    colSpanColumns,
    lastFrozenColumnIndex,
    groupBy
  } = useMemo(() => {
    // Filter rawGroupBy and ignore keys that do not match the columns prop
    const groupBy = [];
    let lastFrozenColumnIndex = -1;
    const columns = rawColumns.map(rawColumn => {
      var _a, _b, _c; // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing


      const frozen = rawColumn.frozen || false;
      const column = Object.assign(Object.assign({}, rawColumn), {
        idx: 0,
        frozen,
        isLastFrozenColumn: false,
        sortable: (_a = rawColumn.sortable) !== null && _a !== void 0 ? _a : defaultSortable,
        resizable: (_b = rawColumn.resizable) !== null && _b !== void 0 ? _b : defaultResizable,
        formatter: (_c = rawColumn.formatter) !== null && _c !== void 0 ? _c : defaultFormatter
      });

      if (frozen) {
        lastFrozenColumnIndex++;
      }

      return column;
    });
    columns.sort(({
      key: aKey,
      frozen: frozenA
    }, {
      key: bKey,
      frozen: frozenB
    }) => {
      // Sort select column first:
      if (aKey === SELECT_COLUMN_KEY) return -1;
      if (bKey === SELECT_COLUMN_KEY) return 1; // Sort frozen columns third:

      if (frozenA) {
        if (frozenB) return 0;
        return -1;
      }

      if (frozenB) return 1; // Sort other columns last:

      return 0;
    });
    const colSpanColumns = [];
    columns.forEach((column, idx) => {
      column.idx = idx;

      if (column.colSpan != null) {
        colSpanColumns.push(column);
      }
    });

    if (lastFrozenColumnIndex !== -1) {
      columns[lastFrozenColumnIndex].isLastFrozenColumn = true;
    }

    return {
      columns,
      colSpanColumns,
      lastFrozenColumnIndex,
      groupBy
    };
  }, [rawColumns, defaultFormatter, defaultResizable, defaultSortable]);
  const {
    layoutCssVars,
    totalColumnWidth,
    totalFrozenColumnWidth,
    columnMetrics
  } = useMemo(() => {
    const columnMetrics = new Map();
    let left = 0;
    let totalColumnWidth = 0;
    let totalFrozenColumnWidth = 0;
    let templateColumns = '';
    let allocatedWidth = 0;
    let unassignedColumnsCount = 0;

    for (const column of columns) {
      let width = getSpecifiedWidth(column, columnWidths, viewportWidth);

      if (width === undefined) {
        unassignedColumnsCount++;
      } else {
        width = clampColumnWidth(width, column, minColumnWidth);
        allocatedWidth += width;
        columnMetrics.set(column, {
          width,
          left: 0
        });
      }
    }

    const unallocatedWidth = viewportWidth - allocatedWidth;
    const unallocatedColumnWidth = unallocatedWidth / unassignedColumnsCount;

    for (const column of columns) {
      let width;

      if (columnMetrics.has(column)) {
        const columnMetric = columnMetrics.get(column);
        columnMetric.left = left;
        ({
          width
        } = columnMetric);
      } else {
        width = clampColumnWidth(unallocatedColumnWidth, column, minColumnWidth);
        columnMetrics.set(column, {
          width,
          left
        });
      }

      totalColumnWidth += width;
      left += width;
      templateColumns += `${width}px `;
    }

    if (lastFrozenColumnIndex !== -1) {
      const columnMetric = columnMetrics.get(columns[lastFrozenColumnIndex]);
      totalFrozenColumnWidth = columnMetric.left + columnMetric.width;
    }

    const layoutCssVars = {
      '--template-columns': templateColumns
    };

    for (let i = 0; i <= lastFrozenColumnIndex; i++) {
      const column = columns[i];
      layoutCssVars[`--frozen-left-${column.idx}`] = `${columnMetrics.get(column).left}px`;
    }

    return {
      layoutCssVars,
      totalColumnWidth,
      totalFrozenColumnWidth,
      columnMetrics
    };
  }, [columnWidths, columns, viewportWidth, minColumnWidth, lastFrozenColumnIndex]);
  const [colOverscanStartIdx, colOverscanEndIdx] = useMemo(() => {
    if (!enableVirtualization) {
      return [0, columns.length - 1];
    } // get the viewport's left side and right side positions for non-frozen columns


    const viewportLeft = scrollLeft + totalFrozenColumnWidth;
    const viewportRight = scrollLeft + viewportWidth; // get first and last non-frozen column indexes

    const lastColIdx = columns.length - 1;
    const firstUnfrozenColumnIdx = min(lastFrozenColumnIndex + 1, lastColIdx); // skip rendering non-frozen columns if the frozen columns cover the entire viewport

    if (viewportLeft >= viewportRight) {
      return [firstUnfrozenColumnIdx, firstUnfrozenColumnIdx];
    } // get the first visible non-frozen column index


    let colVisibleStartIdx = firstUnfrozenColumnIdx;

    while (colVisibleStartIdx < lastColIdx) {
      const {
        left,
        width
      } = columnMetrics.get(columns[colVisibleStartIdx]); // if the right side of the columnn is beyond the left side of the available viewport,
      // then it is the first column that's at least partially visible

      if (left + width > viewportLeft) {
        break;
      }

      colVisibleStartIdx++;
    } // get the last visible non-frozen column index


    let colVisibleEndIdx = colVisibleStartIdx;

    while (colVisibleEndIdx < lastColIdx) {
      const {
        left,
        width
      } = columnMetrics.get(columns[colVisibleEndIdx]); // if the right side of the column is beyond or equal to the right side of the available viewport,
      // then it the last column that's at least partially visible, as the previous column's right side is not beyond the viewport.

      if (left + width >= viewportRight) {
        break;
      }

      colVisibleEndIdx++;
    }

    const colOverscanStartIdx = max(firstUnfrozenColumnIdx, colVisibleStartIdx - 1);
    const colOverscanEndIdx = min(lastColIdx, colVisibleEndIdx + 1);
    return [colOverscanStartIdx, colOverscanEndIdx];
  }, [columnMetrics, columns, lastFrozenColumnIndex, scrollLeft, totalFrozenColumnWidth, viewportWidth, enableVirtualization]);
  return {
    columns,
    colSpanColumns,
    colOverscanStartIdx,
    colOverscanEndIdx,
    layoutCssVars,
    columnMetrics,
    totalColumnWidth,
    lastFrozenColumnIndex,
    totalFrozenColumnWidth,
    groupBy
  };
}

function getSpecifiedWidth({
  key,
  width
}, columnWidths, viewportWidth) {
  if (columnWidths.has(key)) {
    // Use the resized width if available
    return columnWidths.get(key);
  }

  if (typeof width === 'number') {
    return width;
  }

  if (typeof width === 'string' && /^\d+%$/.test(width)) {
    return floor(viewportWidth * parseInt(width, 10) / 100);
  }

  return undefined;
}

function clampColumnWidth(width, {
  minWidth,
  maxWidth
}, minColumnWidth) {
  width = max(width, minWidth !== null && minWidth !== void 0 ? minWidth : minColumnWidth);

  if (typeof maxWidth === 'number') {
    return min(width, maxWidth);
  }

  return width;
}

function useCombinedRefs(...refs) {
  return useCallback(handle => {
    for (const ref of refs) {
      if (typeof ref === 'function') {
        ref(handle);
      } else if (ref !== null && 'current' in ref) {
        // @ts-expect-error: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/31065
        ref.current = handle;
      }
    }
  }, refs);
}

function useGridDimensions() {
  const gridRef = useRef(null);
  const [gridWidth, setGridWidth] = useState(1);
  const [gridHeight, setGridHeight] = useState(1);
  useLayoutEffect(() => {
    const {
      ResizeObserver
    } = window; // don't break in Node.js (SSR), jest/jsdom, and browsers that don't support ResizeObserver
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition

    if (ResizeObserver == null) return;
    const resizeObserver = new ResizeObserver(() => {
      // Get dimensions without scrollbars.
      // The dimensions given by the callback entries in Firefox do not substract the scrollbar sizes.
      const {
        clientWidth,
        clientHeight
      } = gridRef.current; // TODO: remove once fixed upstream
      // we reduce width by 1px here to avoid layout issues in Chrome
      // https://bugs.chromium.org/p/chromium/issues/detail?id=1206298

      setGridWidth(clientWidth - (devicePixelRatio % 1 === 0 ? 0 : 1));
      setGridHeight(clientHeight);
    });
    resizeObserver.observe(gridRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  return [gridRef, gridWidth, gridHeight];
} // https://reactjs.org/docs/hooks-faq.html#what-can-i-do-if-my-effect-dependencies-change-too-often
// eslint-disable-next-line @typescript-eslint/no-explicit-any


function useLatestFunc(fn) {
  const ref = useRef(fn);
  useEffect(() => {
    ref.current = fn;
  });
  return useCallback((...args) => {
    ref.current(...args);
  }, []);
} // https://www.w3.org/TR/wai-aria-practices-1.1/#kbd_roving_tabindex


function useRovingCellRef(isSelected) {
  const ref = useRef(null); // https://www.w3.org/TR/wai-aria-practices-1.1/#gridNav_focus

  const isChildFocused = useRef(false);
  const [, forceRender] = useState({});
  useLayoutEffect(() => {
    var _a;

    if (!isSelected) {
      isChildFocused.current = false;
      return;
    }

    if (isChildFocused.current) {
      // When the child is focused, we need to rerender
      // the cell again so tabIndex is updated to -1
      forceRender({});
      return;
    }

    (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus({
      preventScroll: true
    });
  }, [isSelected]);

  function onFocus(event) {
    if (event.target !== ref.current) {
      isChildFocused.current = true;
    }
  }

  const isFocused = isSelected && !isChildFocused.current;
  return {
    ref,
    tabIndex: isFocused ? 0 : -1,
    onFocus
  };
}

const rowSelectedClassname = `rdt-row-selected`;

function useRovingRowRef(selectedCellIdx) {
  const isSelected = selectedCellIdx === -1;
  const {
    ref,
    tabIndex
  } = useFocusRef(isSelected);
  return {
    ref,
    tabIndex,
    className: isSelected ? rowSelectedClassname : undefined
  };
}

function useViewportColumns({
  columns,
  colSpanColumns,
  rows,
  summaryRows,
  colOverscanStartIdx,
  colOverscanEndIdx,
  lastFrozenColumnIndex,
  rowOverscanStartIdx,
  rowOverscanEndIdx
}) {
  // find the column that spans over a column within the visible columns range and adjust colOverscanStartIdx
  const startIdx = useMemo(() => {
    if (colOverscanStartIdx === 0) return 0;
    let startIdx = colOverscanStartIdx;

    const updateStartIdx = (colIdx, colSpan) => {
      if (colSpan !== undefined && colIdx + colSpan > colOverscanStartIdx) {
        startIdx = colIdx;
        return true;
      }

      return false;
    };

    for (const column of colSpanColumns) {
      // check header row
      const colIdx = column.idx;
      if (colIdx >= startIdx) break;

      if (updateStartIdx(colIdx, getColSpan(column, lastFrozenColumnIndex, {
        type: 'HEADER'
      }))) {
        break;
      } // check viewport rows


      for (let rowIdx = rowOverscanStartIdx; rowIdx <= rowOverscanEndIdx; rowIdx++) {
        const row = rows[rowIdx];

        if (updateStartIdx(colIdx, getColSpan(column, lastFrozenColumnIndex, {
          type: 'ROW',
          row
        }))) {
          break;
        }
      } // check summary rows


      if (summaryRows != null) {
        for (const row of summaryRows) {
          if (updateStartIdx(colIdx, getColSpan(column, lastFrozenColumnIndex, {
            type: 'SUMMARY',
            row
          }))) {
            break;
          }
        }
      }
    }

    return startIdx;
  }, [rowOverscanStartIdx, rowOverscanEndIdx, rows, summaryRows, colOverscanStartIdx, lastFrozenColumnIndex, colSpanColumns]);
  return useMemo(() => {
    const viewportColumns = [];

    for (let colIdx = 0; colIdx <= colOverscanEndIdx; colIdx++) {
      const column = columns[colIdx];
      if (colIdx < startIdx && !column.frozen) continue;
      viewportColumns.push(column);
    }

    return viewportColumns;
  }, [startIdx, colOverscanEndIdx, columns]);
}

function useViewportRows({
  rawRows: rows,
  rowHeight,
  clientHeight,
  scrollTop,
  enableVirtualization
}) {
  const {
    totalRowHeight,
    getRowTop,
    getRowHeight,
    findRowIdx
  } = useMemo(() => {
    if (typeof rowHeight === 'number') {
      return {
        totalRowHeight: rowHeight * rows.length,
        getRowTop: rowIdx => rowIdx * rowHeight,
        getRowHeight: () => rowHeight,
        findRowIdx: offset => floor(offset / rowHeight)
      };
    } // TODO: Handle the rowHeight string value correctly


    if (typeof rowHeight === 'string') {
      return {
        totalRowHeight: 0,
        getRowTop: () => 0,
        getRowHeight: () => rowHeight,
        findRowIdx: () => 0
      };
    }

    let totalRowHeight = 0; // Calcule the height of all the rows upfront. This can cause performance issues
    // and we can consider using a similar approach as react-window
    // https://github.com/bvaughn/react-window/blob/b0a470cc264e9100afcaa1b78ed59d88f7914ad4/src/VariableSizeList.js#L68

    const rowPositions = rows.map(row => {
      const currentRowHeight = rowHeight({
        type: 'ROW',
        row
      });
      const position = {
        top: totalRowHeight,
        height: currentRowHeight
      };

      if (typeof currentRowHeight === 'number') {
        totalRowHeight += currentRowHeight;
      }

      return position;
    });

    const validateRowIdx = rowIdx => {
      return max(0, min(rows.length - 1, rowIdx));
    };

    return {
      totalRowHeight,
      getRowTop: rowIdx => rowPositions[validateRowIdx(rowIdx)].top,
      getRowHeight: rowIdx => rowPositions[validateRowIdx(rowIdx)].height,

      findRowIdx(offset) {
        let start = 0;
        let end = rowPositions.length - 1;

        while (start <= end) {
          const middle = start + floor((end - start) / 2);
          const currentOffset = rowPositions[middle].top;
          if (currentOffset === offset) return middle;

          if (currentOffset < offset) {
            start = middle + 1;
          } else if (currentOffset > offset) {
            end = middle - 1;
          }

          if (start > end) return end;
        }

        return 0;
      }

    };
  }, [rowHeight, rows]);
  let rowOverscanStartIdx = 0;
  let rowOverscanEndIdx = rows.length - 1;

  if (enableVirtualization) {
    const overscanThreshold = 4;
    const rowVisibleStartIdx = findRowIdx(scrollTop);
    const rowVisibleEndIdx = findRowIdx(scrollTop + clientHeight);
    rowOverscanStartIdx = max(0, rowVisibleStartIdx - overscanThreshold);
    rowOverscanEndIdx = min(rows.length - 1, rowVisibleEndIdx + overscanThreshold);
  }

  return {
    rowOverscanStartIdx,
    rowOverscanEndIdx,
    rows,
    rowsCount: rows.length,
    totalRowHeight,
    getRowTop,
    getRowHeight,
    findRowIdx
  };
}
/*
 * To check for outside `mousedown` events, we listen to all `mousedown` events at their birth,
 * i.e. on the window during the capture phase, and at their death, i.e. on the window during the bubble phase.
 *
 * We schedule a check at the birth of the event, cancel the check when the event reaches the "inside" container,
 * and trigger the "outside" callback when the event bubbles back up to the window.
 *
 * The event can be `stopPropagation()`ed halfway through, so they may not always bubble back up to the window,
 * so an alternative check must be used. The check must happen after the event can reach the "inside" container,
 * and not before it run to completion. `requestAnimationFrame` is the best way we know how to achieve this.
 * Usually we want click event handlers from parent components to access the latest commited values,
 * so `mousedown` is used instead of `click`.
 *
 * We must also rely on React's event capturing/bubbling to handle elements rendered in a portal.
 */


const cellEditingClassname = `rdt-cell-editing p-0`;

function EditCell({
  column,
  colSpan,
  row,
  onRowChange,
  closeEditor,
  scrollToCell
}) {
  var _a, _b, _c;

  const frameRequestRef = useRef();
  const commitOnOutsideClick = ((_a = column.editorOptions) === null || _a === void 0 ? void 0 : _a.commitOnOutsideClick) !== false; // We need to prevent the `useEffect` from cleaning up between re-renders,
  // as `onWindowCaptureMouseDown` might otherwise miss valid mousedown events.
  // To that end we instead access the latest props via useLatestFunc.

  const commitOnOutsideMouseDown = useLatestFunc(() => {
    onClose(true);
  });
  useEffect(() => {
    if (!commitOnOutsideClick) return;

    function onWindowCaptureMouseDown() {
      frameRequestRef.current = requestAnimationFrame(commitOnOutsideMouseDown);
    }

    addEventListener('mousedown', onWindowCaptureMouseDown, {
      capture: true
    });
    return () => {
      removeEventListener('mousedown', onWindowCaptureMouseDown, {
        capture: true
      });
      cancelFrameRequest();
    };
  }, [commitOnOutsideClick, commitOnOutsideMouseDown]);

  function cancelFrameRequest() {
    cancelAnimationFrame(frameRequestRef.current);
  }

  function onKeyDown(event) {
    var _a, _b;

    if (event.key === 'Escape') {
      event.stopPropagation(); // Discard changes

      onClose(true);
    } else if (event.key === 'Enter') {
      event.stopPropagation();
      onClose(true);
      scrollToCell();
    } else {
      const onNavigation = (_b = (_a = column.editorOptions) === null || _a === void 0 ? void 0 : _a.onNavigation) !== null && _b !== void 0 ? _b : onEditorNavigation;

      if (!onNavigation(event)) {
        event.stopPropagation();
      }
    }
  }

  function onClose(commitChanges) {
    if (commitChanges) {
      onRowChange(row, true);
    } else {
      closeEditor();
    }
  }

  const {
    cellClass
  } = column;
  const className = getCellClassname(column, 'rdt-editor-container', !((_b = column.editorOptions) === null || _b === void 0 ? void 0 : _b.renderFormatter) && cellEditingClassname, typeof cellClass === 'function' ? cellClass(row) : cellClass);
  return /*#__PURE__*/React.createElement("div", {
    role: "gridcell",
    "aria-colindex": column.idx + 1,
    "aria-colspan": colSpan,
    "aria-selected": true,
    className: className,
    style: getCellStyle(column, colSpan),
    onKeyDown: onKeyDown,
    onMouseDownCapture: commitOnOutsideClick ? cancelFrameRequest : undefined
  }, column.editor != null && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(column.editor, {
    column: column,
    row: row,
    onRowChange: onRowChange,
    onClose: onClose
  }), ((_c = column.editorOptions) === null || _c === void 0 ? void 0 : _c.renderFormatter) && /*#__PURE__*/React.createElement(column.formatter, {
    column: column,
    row: row,
    isCellSelected: true,
    onRowChange: onRowChange
  })));
}

const headerSortCellClassname = `rdt-header-sort-cell`;
const headerSortNameClassname = `rdt-header-sort-name`;
const arrowClassname = `rdt-sort-arrow`;

function SortableHeaderCell({
  onSort,
  sortDirection,
  priority,
  children,
  isCellSelected
}) {
  const {
    ref,
    tabIndex
  } = useFocusRef(isCellSelected);

  function handleKeyDown(event) {
    if (event.key === ' ' || event.key === 'Enter') {
      // stop propagation to prevent scrolling
      event.preventDefault();
      onSort(event.ctrlKey || event.metaKey);
    }
  }

  function handleClick(event) {
    onSort(event.ctrlKey || event.metaKey);
  }

  return /*#__PURE__*/React.createElement("span", {
    ref: ref,
    tabIndex: tabIndex,
    className: headerSortCellClassname,
    onClick: handleClick,
    onKeyDown: handleKeyDown
  }, /*#__PURE__*/React.createElement("span", {
    className: headerSortNameClassname
  }, children), /*#__PURE__*/React.createElement("span", null, sortDirection !== undefined && /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 12 8",
    width: "12",
    height: "8",
    className: arrowClassname,
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement("path", {
    d: sortDirection === 'ASC' ? 'M0 8 6 0 12 8' : 'M0 0 6 8 12 0'
  })), priority));
}

const cellResizableClassname = `rdt-cell-resizable`;

function HeaderCell({
  column,
  colSpan,
  isCellSelected,
  onColumnResize,
  allRowsSelected,
  onAllRowsSelectionChange,
  sortColumns,
  onSortColumnsChange,
  selectCell,
  shouldFocusGrid
}) {
  const {
    ref,
    tabIndex,
    onFocus
  } = useRovingCellRef(isCellSelected);
  const sortIndex = sortColumns === null || sortColumns === void 0 ? void 0 : sortColumns.findIndex(sort => sort.columnKey === column.key);
  const sortColumn = sortIndex !== undefined && sortIndex > -1 ? sortColumns[sortIndex] : undefined;
  const sortDirection = sortColumn === null || sortColumn === void 0 ? void 0 : sortColumn.direction;
  const priority = sortColumn !== undefined && sortColumns.length > 1 ? sortIndex + 1 : undefined;
  const ariaSort = sortDirection && !priority ? sortDirection === 'ASC' ? 'ascending' : 'descending' : undefined;
  const className = getCellClassname(column, column.headerCellClass, {
    [cellResizableClassname]: column.resizable
  });

  function onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.buttons !== 1) {
      return;
    }

    const {
      currentTarget,
      pointerId
    } = event;
    const {
      right
    } = currentTarget.getBoundingClientRect();
    const offset = right - event.clientX;

    if (offset > 11) {
      // +1px to account for the border size
      return;
    }

    function onPointerMove(event) {
      const width = event.clientX + offset - currentTarget.getBoundingClientRect().left;

      if (width > 0) {
        onColumnResize(column, width);
      }
    }

    function onLostPointerCapture() {
      currentTarget.removeEventListener('pointermove', onPointerMove);
      currentTarget.removeEventListener('lostpointercapture', onLostPointerCapture);
    }

    currentTarget.setPointerCapture(pointerId);
    currentTarget.addEventListener('pointermove', onPointerMove);
    currentTarget.addEventListener('lostpointercapture', onLostPointerCapture);
  }

  function onSort(ctrlClick) {
    if (onSortColumnsChange == null) return;
    const {
      sortDescendingFirst
    } = column;

    if (sortColumn === undefined) {
      // not currently sorted
      const nextSort = {
        columnKey: column.key,
        direction: sortDescendingFirst ? 'DESC' : 'ASC'
      };
      onSortColumnsChange(sortColumns && ctrlClick ? [...sortColumns, nextSort] : [nextSort]);
    } else {
      let nextSortColumn;

      if (sortDescendingFirst && sortDirection === 'DESC' || !sortDescendingFirst && sortDirection === 'ASC') {
        nextSortColumn = {
          columnKey: column.key,
          direction: sortDirection === 'ASC' ? 'DESC' : 'ASC'
        };
      }

      if (ctrlClick) {
        const nextSortColumns = [...sortColumns];

        if (nextSortColumn) {
          // swap direction
          nextSortColumns[sortIndex] = nextSortColumn;
        } else {
          // remove sort
          nextSortColumns.splice(sortIndex, 1);
        }

        onSortColumnsChange(nextSortColumns);
      } else {
        onSortColumnsChange(nextSortColumn ? [nextSortColumn] : []);
      }
    }
  }

  function onClick() {
    selectCell(column.idx);
  }

  function handleFocus(event) {
    onFocus(event);

    if (shouldFocusGrid) {
      // Select the first header cell if there is no selected cell
      selectCell(0);
    }
  }

  function getCell() {
    if (column.headerRenderer) {
      return /*#__PURE__*/React.createElement(column.headerRenderer, {
        column: column,
        sortDirection: sortDirection,
        priority: priority,
        onSort: onSort,
        allRowsSelected: allRowsSelected,
        onAllRowsSelectionChange: onAllRowsSelectionChange,
        isCellSelected: isCellSelected
      });
    }

    if (column.sortable) {
      return /*#__PURE__*/React.createElement(SortableHeaderCell, {
        onSort: onSort,
        sortDirection: sortDirection,
        priority: priority,
        isCellSelected: isCellSelected
      }, column.name);
    }

    return column.name;
  }

  return /*#__PURE__*/React.createElement("div", {
    role: "columnheader",
    "aria-colindex": column.idx + 1,
    "aria-selected": isCellSelected,
    "aria-sort": ariaSort,
    "aria-colspan": colSpan,
    ref: ref,
    // set the tabIndex to 0 when there is no selected cell so grid can receive focus
    tabIndex: shouldFocusGrid ? 0 : tabIndex,
    className: className,
    style: getCellStyle(column, colSpan),
    onFocus: handleFocus,
    onClick: onClick,
    onPointerDown: column.resizable ? onPointerDown : undefined
  }, getCell());
}

const headerRowClassname = `rdt-header-row`;

function HeaderRow({
  columns,
  allRowsSelected,
  onAllRowsSelectionChange,
  onColumnResize,
  sortColumns,
  onSortColumnsChange,
  lastFrozenColumnIndex,
  selectedCellIdx,
  selectCell,
  shouldFocusGrid
}) {
  const {
    ref,
    tabIndex,
    className
  } = useRovingRowRef(selectedCellIdx);
  const cells = [];

  for (let index = 0; index < columns.length; index++) {
    const column = columns[index];
    const colSpan = getColSpan(column, lastFrozenColumnIndex, {
      type: 'HEADER'
    });

    if (colSpan !== undefined) {
      index += colSpan - 1;
    }

    cells.push( /*#__PURE__*/React.createElement(HeaderCell, {
      key: column.key,
      column: column,
      colSpan: colSpan,
      isCellSelected: selectedCellIdx === column.idx,
      onColumnResize: onColumnResize,
      allRowsSelected: allRowsSelected,
      onAllRowsSelectionChange: onAllRowsSelectionChange,
      onSortColumnsChange: onSortColumnsChange,
      sortColumns: sortColumns,
      selectCell: selectCell,
      shouldFocusGrid: shouldFocusGrid && index === 0
    }));
  }

  return /*#__PURE__*/React.createElement("div", {
    role: "row",
    "aria-rowindex": 1,
    ref: ref,
    tabIndex: tabIndex,
    className: clsx(headerRowClassname, className)
  }, cells);
}

var HeaderRow$1 = /*#__PURE__*/memo(HeaderRow);
/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

const cellCopiedClassname = `rdt-cell-copied`;
const cellDraggedOverClassname = `rdt-cell-dragged-over`;

function Cell(_a) {
  var {
    column,
    colSpan,
    isCellSelected,
    isCopied,
    isDraggedOver,
    row,
    dragHandle,
    onRowClick,
    onRowDoubleClick,
    onRowChange,
    selectCell
  } = _a,
      props = __rest(_a, ["column", "colSpan", "isCellSelected", "isCopied", "isDraggedOver", "row", "dragHandle", "onRowClick", "onRowDoubleClick", "onRowChange", "selectCell"]);

  const {
    ref,
    tabIndex,
    onFocus
  } = useRovingCellRef(isCellSelected);
  const {
    cellClass
  } = column;
  const className = getCellClassname(column, {
    [cellCopiedClassname]: isCopied,
    [cellDraggedOverClassname]: isDraggedOver
  }, typeof cellClass === 'function' ? cellClass(row) : cellClass);

  function selectCellWrapper(openEditor) {
    selectCell(row, column, openEditor);
  }

  function handleClick() {
    var _a;

    selectCellWrapper((_a = column.editorOptions) === null || _a === void 0 ? void 0 : _a.editOnClick);
    onRowClick === null || onRowClick === void 0 ? void 0 : onRowClick(row, column);
  }

  function handleContextMenu() {
    selectCellWrapper();
  }

  function handleDoubleClick() {
    selectCellWrapper(true);
    onRowDoubleClick === null || onRowDoubleClick === void 0 ? void 0 : onRowDoubleClick(row, column);
  }

  return /*#__PURE__*/React.createElement("div", Object.assign({
    role: "gridcell",
    "aria-colindex": column.idx + 1,
    "aria-selected": isCellSelected,
    "aria-colspan": colSpan,
    "aria-readonly": !isCellEditable(column, row) || undefined,
    ref: ref,
    tabIndex: tabIndex,
    className: className,
    style: getCellStyle(column, colSpan),
    onClick: handleClick,
    onDoubleClick: handleDoubleClick,
    onContextMenu: handleContextMenu,
    onFocus: onFocus
  }, props), /*#__PURE__*/React.createElement(column.formatter, {
    column: column,
    row: row,
    isCellSelected: isCellSelected,
    onRowChange: onRowChange
  }), dragHandle);
}

var Cell$1 = /*#__PURE__*/memo(Cell);

function Row(_a, ref) {
  var {
    className,
    rowIdx,
    selectedCellIdx,
    isRowSelected,
    copiedCellIdx,
    draggedOverCellIdx,
    lastFrozenColumnIndex,
    row,
    viewportColumns,
    selectedCellEditor,
    selectedCellDragHandle,
    onRowClick,
    onRowDoubleClick,
    rowClass,
    setDraggedOverRowIdx,
    onMouseEnter,
    top,
    height,
    onRowChange,
    selectCell
  } = _a,
      props = __rest(_a, ["className", "rowIdx", "selectedCellIdx", "isRowSelected", "copiedCellIdx", "draggedOverCellIdx", "lastFrozenColumnIndex", "row", "viewportColumns", "selectedCellEditor", "selectedCellDragHandle", "onRowClick", "onRowDoubleClick", "rowClass", "setDraggedOverRowIdx", "onMouseEnter", "top", "height", "onRowChange", "selectCell"]);

  const {
    ref: rowRef,
    tabIndex,
    className: rovingClassName
  } = useRovingRowRef(selectedCellIdx);
  const handleRowChange = useLatestFunc(newRow => {
    onRowChange(rowIdx, newRow);
  });

  function handleDragEnter(event) {
    setDraggedOverRowIdx === null || setDraggedOverRowIdx === void 0 ? void 0 : setDraggedOverRowIdx(rowIdx);
    onMouseEnter === null || onMouseEnter === void 0 ? void 0 : onMouseEnter(event);
  }

  className = clsx(rowClassname, `rdt-row-${rowIdx % 2 === 0 ? 'even' : 'odd'}`, rovingClassName, rowClass === null || rowClass === void 0 ? void 0 : rowClass(row), className);
  const cells = [];

  for (let index = 0; index < viewportColumns.length; index++) {
    const column = viewportColumns[index];
    const {
      idx
    } = column;
    const colSpan = getColSpan(column, lastFrozenColumnIndex, {
      type: 'ROW',
      row
    });

    if (colSpan !== undefined) {
      index += colSpan - 1;
    }

    const isCellSelected = selectedCellIdx === idx;

    if (isCellSelected && selectedCellEditor) {
      cells.push(selectedCellEditor);
    } else {
      cells.push( /*#__PURE__*/React.createElement(Cell$1, {
        key: column.key,
        column: column,
        colSpan: colSpan,
        row: row,
        isCopied: copiedCellIdx === idx,
        isDraggedOver: draggedOverCellIdx === idx,
        isCellSelected: isCellSelected,
        dragHandle: isCellSelected ? selectedCellDragHandle : undefined,
        onRowClick: onRowClick,
        onRowDoubleClick: onRowDoubleClick,
        onRowChange: handleRowChange,
        selectCell: selectCell
      }));
    }
  }

  return /*#__PURE__*/React.createElement(RowSelectionProvider, {
    value: isRowSelected
  }, /*#__PURE__*/React.createElement("div", Object.assign({
    role: "row",
    ref: useCombinedRefs(ref, rowRef),
    tabIndex: tabIndex,
    className: className,
    onMouseEnter: handleDragEnter,
    style: {
      top,
      '--row-height': typeof height === 'number' ? `${height}px` : height
    }
  }, props), cells));
}

var Row$1 = /*#__PURE__*/memo(Row);
const RowWithRef = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(Row));

const SelectionRange = props => {
  return /*#__PURE__*/React.createElement("div", {
    className: "w-0 h-0",
    tabIndex: 0
  }, /*#__PURE__*/React.createElement("div", {
    className: "h-100 bg-blue-300 w-100 absolute top-0 pointer-events-none",
    style: Object.assign({
      backgroundColor: '#66afe930',
      border: '1px solid #66afe9'
    }, props)
  }));
};

function SummaryCell({
  column,
  colSpan,
  row,
  isCellSelected,
  selectCell
}) {
  const {
    ref,
    tabIndex,
    onFocus
  } = useRovingCellRef(isCellSelected);
  const {
    summaryFormatter: SummaryFormatter,
    summaryCellClass
  } = column;
  const className = getCellClassname(column, typeof summaryCellClass === 'function' ? summaryCellClass(row) : summaryCellClass);

  function onClick() {
    selectCell(row, column);
  }

  return /*#__PURE__*/React.createElement("div", {
    role: "gridcell",
    "aria-colindex": column.idx + 1,
    "aria-colspan": colSpan,
    "aria-selected": isCellSelected,
    ref: ref,
    tabIndex: tabIndex,
    className: className,
    style: getCellStyle(column, colSpan),
    onClick: onClick,
    onFocus: onFocus
  }, SummaryFormatter && /*#__PURE__*/React.createElement(SummaryFormatter, {
    column: column,
    row: row,
    isCellSelected: isCellSelected
  }));
}

var SummaryCell$1 = /*#__PURE__*/memo(SummaryCell);
const summaryRowClassname = `rdt-summary-row`;

function SummaryRow({
  rowIdx,
  row,
  viewportColumns,
  bottom,
  lastFrozenColumnIndex,
  selectedCellIdx,
  selectCell,
  'aria-rowindex': ariaRowIndex
}) {
  const {
    ref,
    tabIndex,
    className
  } = useRovingRowRef(selectedCellIdx);
  const cells = [];

  for (let index = 0; index < viewportColumns.length; index++) {
    const column = viewportColumns[index];
    const colSpan = getColSpan(column, lastFrozenColumnIndex, {
      type: 'SUMMARY',
      row
    });

    if (colSpan !== undefined) {
      index += colSpan - 1;
    }

    const isCellSelected = selectedCellIdx === column.idx;
    cells.push( /*#__PURE__*/React.createElement(SummaryCell$1, {
      key: column.key,
      column: column,
      colSpan: colSpan,
      row: row,
      isCellSelected: isCellSelected,
      selectCell: selectCell
    }));
  }

  return /*#__PURE__*/React.createElement("div", {
    role: "row",
    "aria-rowindex": ariaRowIndex,
    ref: ref,
    tabIndex: tabIndex,
    className: clsx(rowClassname, `rdt-row-${rowIdx % 2 === 0 ? 'even' : 'odd'}`, summaryRowClassname, className),
    style: {
      bottom
    }
  }, cells);
}

var SummaryRow$1 = /*#__PURE__*/memo(SummaryRow);
const initialPosition = {
  idx: -1,
  rowIdx: -2,
  mode: 'SELECT'
};
const initialRange = {
  topLeft: {
    idx: -1,
    rowIdx: -1
  },
  bottomRight: {
    idx: -1,
    rowIdx: -1
  },
  startCell: null,
  cursorCell: null
};
/**
 * Main API Component to render a data grid of rows and columns
 *
 * @example
 *
 * <DataGrid columns={columns} rows={rows} />
 */

function DataGrid({
  // Grid and data Props
  columns: rawColumns,
  rows: rawRows,
  summaryRows,
  rowKeyGetter,
  onRowsChange,
  // Dimensions props
  rowHeight,
  headerRowHeight: rawHeaderRowHeight,
  summaryRowHeight: rawSummaryRowHeight,
  // Feature props
  selectedRows,
  onSelectedRowsChange,
  sortColumns,
  onSortColumnsChange,
  defaultColumnOptions,
  // Event props
  onRowClick,
  onRowDoubleClick,
  onScroll,
  onColumnResize,
  onFill,
  onPaste,
  onCopy,
  // Toggles and modes
  cellNavigationMode: rawCellNavigationMode,
  enableVirtualization,
  // Miscellaneous
  rowRenderer,
  noRowsFallback,
  className,
  style,
  rowClass,
  // ARIA
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'data-testid': testId,
  'data-gridid': gridId
}, ref) {
  var _a;
  /**
   * defaults
   */


  rowHeight !== null && rowHeight !== void 0 ? rowHeight : rowHeight = 35;
  const headerRowHeight = rawHeaderRowHeight !== null && rawHeaderRowHeight !== void 0 ? rawHeaderRowHeight : typeof rowHeight === 'number' ? rowHeight : 35;
  const summaryRowHeight = rawSummaryRowHeight !== null && rawSummaryRowHeight !== void 0 ? rawSummaryRowHeight : typeof rowHeight === 'number' ? rowHeight : 35;
  const RowRenderer = rowRenderer !== null && rowRenderer !== void 0 ? rowRenderer : Row$1;
  const cellNavigationMode = rawCellNavigationMode !== null && rawCellNavigationMode !== void 0 ? rawCellNavigationMode : 'NONE';
  enableVirtualization !== null && enableVirtualization !== void 0 ? enableVirtualization : enableVirtualization = true;
  /**
   * states
   */

  const [scrollTop, setScrollTop] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [columnWidths, setColumnWidths] = useState(() => new Map());
  const [selectedPosition, setSelectedPosition] = useState(initialPosition);
  const [selectedRange, setSelectedRange] = useState(initialRange);
  const [copiedCell, setCopiedCell] = useState(null);
  const [isDragging, setDragging] = useState(false);
  const [draggedOverRowIdx, setOverRowIdx] = useState(undefined);
  /**
   * refs
   */

  const prevSelectedPosition = useRef(selectedPosition);
  const latestDraggedOverRowIdx = useRef(draggedOverRowIdx);
  const lastSelectedRowIdx = useRef(-1);
  /**
   * computed values
   */

  const [gridRef, gridWidth, gridHeight] = useGridDimensions();
  const headerRowsCount = 1;
  const summaryRowsCount = (_a = summaryRows === null || summaryRows === void 0 ? void 0 : summaryRows.length) !== null && _a !== void 0 ? _a : 0;
  const clientHeight = gridHeight - headerRowHeight - summaryRowsCount * summaryRowHeight;
  const isSelectable = selectedRows != null && onSelectedRowsChange != null;
  const isHeaderRowSelected = selectedPosition.rowIdx === -1;
  const allRowsSelected = useMemo(() => {
    // no rows to select = explicitely unchecked
    const {
      length
    } = rawRows;
    return length !== 0 && selectedRows != null && rowKeyGetter != null && selectedRows.size >= length && rawRows.every(row => selectedRows.has(rowKeyGetter(row)));
  }, [rawRows, selectedRows, rowKeyGetter]);
  const {
    columns,
    colSpanColumns,
    colOverscanStartIdx,
    colOverscanEndIdx,
    layoutCssVars,
    columnMetrics,
    totalColumnWidth,
    lastFrozenColumnIndex,
    totalFrozenColumnWidth
  } = useCalculatedColumns({
    rawColumns,
    columnWidths,
    scrollLeft,
    viewportWidth: gridWidth,
    defaultColumnOptions,
    enableVirtualization
  });
  const {
    rowOverscanStartIdx,
    rowOverscanEndIdx,
    rows,
    rowsCount,
    totalRowHeight,
    getRowTop,
    getRowHeight,
    findRowIdx
  } = useViewportRows({
    rawRows,
    rowHeight,
    clientHeight,
    scrollTop,
    enableVirtualization
  });
  const viewportColumns = useViewportColumns({
    columns,
    colSpanColumns,
    colOverscanStartIdx,
    colOverscanEndIdx,
    lastFrozenColumnIndex,
    rowOverscanStartIdx,
    rowOverscanEndIdx,
    rows,
    summaryRows
  });
  const minColIdx = 1;
  const maxColIdx = columns.length;
  const minRowIdx = 0;
  const maxRowIdx = headerRowsCount + rows.length + summaryRowsCount - 2;
  const selectedCellIsWithinSelectionBounds = isCellWithinSelectionBounds(selectedPosition);
  const selectedCellIsWithinViewportBounds = isCellWithinViewportBounds(selectedPosition);
  /**
   * The identity of the wrapper function is stable so it won't break memoization
   */

  const selectRowLatest = useLatestFunc(selectRow);
  const selectAllRowsLatest = useLatestFunc(selectAllRows);
  const handleFormatterRowChangeLatest = useLatestFunc(updateRow);
  const selectViewportCellLatest = useLatestFunc((row, column, enableEditor) => {
    const rowIdx = rows.indexOf(row);
    selectCell({
      rowIdx,
      idx: column.idx
    }, enableEditor);
  });
  const selectHeaderCellLatest = useLatestFunc(idx => {
    selectCell({
      rowIdx: -1,
      idx
    });
  });
  const selectSummaryCellLatest = useLatestFunc((summaryRow, column) => {
    const rowIdx = summaryRows.indexOf(summaryRow) + headerRowsCount + rows.length - 1;
    selectCell({
      rowIdx,
      idx: column.idx
    });
  });
  /**
   * effects
   */

  useLayoutEffect(() => {
    if (!selectedCellIsWithinSelectionBounds || isSamePosition(selectedPosition, prevSelectedPosition.current)) {
      prevSelectedPosition.current = selectedPosition;
      return;
    }

    prevSelectedPosition.current = selectedPosition;
    scrollToCell(selectedPosition);
  });
  useImperativeHandle(ref, () => ({
    element: gridRef.current,

    scrollToColumn(idx) {
      scrollToCell({
        idx
      });
    },

    scrollToRow(rowIdx) {
      const {
        current
      } = gridRef;
      if (!current) return;
      current.scrollTo({
        top: getRowTop(rowIdx),
        behavior: 'smooth'
      });
    },

    selectCell
  }));
  useOnclickOutside(() => {
    setTimeout(() => {
      setSelectedPosition(initialPosition);
    }, 500);
  }, {
    refs: [gridRef]
  });
  /**
   * callbacks
   */

  const handleColumnResize = useCallback((column, width) => {
    setColumnWidths(columnWidths => {
      const newColumnWidths = new Map(columnWidths);
      newColumnWidths.set(column.key, width);
      return newColumnWidths;
    });
    onColumnResize === null || onColumnResize === void 0 ? void 0 : onColumnResize(column.idx, width);
  }, [onColumnResize]);
  const setDraggedOverRowIdx = useCallback(rowIdx => {
    setOverRowIdx(rowIdx);
    latestDraggedOverRowIdx.current = rowIdx;
  }, []);
  /**
   * event handlers
   */

  function selectRow({
    row,
    checked,
    isShiftClick
  }) {
    if (!onSelectedRowsChange) return;
    assertIsValidKeyGetter(rowKeyGetter);
    const newSelectedRows = new Set(selectedRows);
    const rowKey = rowKeyGetter(row);

    if (checked) {
      newSelectedRows.add(rowKey);
      const previousRowIdx = lastSelectedRowIdx.current;
      const rowIdx = rows.indexOf(row);
      lastSelectedRowIdx.current = rowIdx;

      if (isShiftClick && previousRowIdx !== -1 && previousRowIdx !== rowIdx) {
        const step = sign(rowIdx - previousRowIdx);

        for (let i = previousRowIdx + step; i !== rowIdx; i += step) {
          const row = rows[i];
          newSelectedRows.add(rowKeyGetter(row));
        }
      }
    } else {
      newSelectedRows.delete(rowKey);
      lastSelectedRowIdx.current = -1;
    }

    onSelectedRowsChange(newSelectedRows);
  }

  function selectAllRows(checked) {
    if (!onSelectedRowsChange) return;
    assertIsValidKeyGetter(rowKeyGetter);
    const newSelectedRows = new Set(selectedRows);

    for (const row of rawRows) {
      const rowKey = rowKeyGetter(row);

      if (checked) {
        newSelectedRows.add(rowKey);
      } else {
        newSelectedRows.delete(rowKey);
      }
    }

    onSelectedRowsChange(newSelectedRows);
  }

  function onSelectCellRangeUpdated(cellPosition) {
    if (!isCellWithinSelectionBounds(cellPosition)) return;
    const startCell = selectedRange.startCell || selectedPosition;
    const colIdxs = [startCell.idx, cellPosition.idx].sort((a, b) => a - b);
    const rowIdxs = [startCell.rowIdx, cellPosition.rowIdx].sort((a, b) => a - b);
    const topLeft = {
      idx: colIdxs[0],
      rowIdx: rowIdxs[0]
    };
    const bottomRight = {
      idx: colIdxs[1],
      rowIdx: rowIdxs[1]
    };
    setSelectedRange(Object.assign(Object.assign({}, selectedRange), {
      topLeft,
      bottomRight,
      startCell: startCell,
      cursorCell: cellPosition
    }));
  }

  function handleChangeSelectionRange(event) {
    const currentPosition = selectedRange.cursorCell || selectedPosition;
    const nextPosition = getNextPosition(event.key, event.ctrlKey, event.shiftKey, currentPosition);
    const nextSelectedCellPosition = getNextSelectedCellPosition({
      columns,
      colSpanColumns,
      rows,
      summaryRows,
      minRowIdx,
      maxRowIdx,
      lastFrozenColumnIndex,
      cellNavigationMode: 'NONE',
      currentPosition: currentPosition,
      nextPosition,
      isCellWithinBounds: isCellWithinSelectionBounds
    });
    onSelectCellRangeUpdated(nextSelectedCellPosition);
  }

  function handleKeyDown(event) {
    if (!(event.target instanceof Element)) return;
    const isCellEvent = event.target.closest('.rdt-cell') !== null; // const isRowEvent = event.target.matches('.rdt-row, .rdt-header-row');

    if (!isCellEvent) return;

    if (selectedCellIsWithinViewportBounds && onPaste != null && isCtrlKeyHeldDown(event) && selectedPosition.mode === 'SELECT') {
      if (isHotKey('mod+c', event)) {
        handleCopy();
        return;
      }

      if (isHotKey('mod+v', event) && copiedCell) {
        handlePaste();
        return;
      }
    }

    if (isHotKey('shift+arrowdown', event) || isHotKey('shift+arrowup', event) || isHotKey('shift+arrowleft', event) || isHotKey('shift+arrowright', event)) {
      handleChangeSelectionRange(event);
      return;
    }

    switch (event.key) {
      case 'Escape':
        setCopiedCell(null);
        handleEditorRowChange(rows[selectedPosition.rowIdx], true, true);
        setSelectedRange(initialRange);
        return;

      case 'ArrowUp':
      case 'ArrowDown':
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Tab':
      case 'Home':
      case 'End':
      case 'PageUp':
      case 'PageDown':
        navigate(event);
        setSelectedRange(initialRange);
        break;

      default:
        handleCellInput(event);
        break;
    }
  }

  function handleScroll(event) {
    const {
      scrollTop,
      scrollLeft
    } = event.currentTarget;
    setScrollTop(scrollTop);
    setScrollLeft(scrollLeft);
    onScroll === null || onScroll === void 0 ? void 0 : onScroll(event);
  }

  function getRawRowIdx(rowIdx) {
    return rowIdx;
  }

  function updateRow(rowIdx, row) {
    if (typeof onRowsChange !== 'function') return;
    const rawRowIdx = getRawRowIdx(rowIdx);
    if (row === rawRows[rawRowIdx]) return;
    const updatedRows = [...rawRows];
    updatedRows[rawRowIdx] = row;
    onRowsChange(updatedRows, {
      indexes: [rawRowIdx],
      column: columns[selectedPosition.idx]
    });
  }

  function commitEditorChanges() {
    if (selectedPosition.mode !== 'EDIT') return;
    updateRow(selectedPosition.rowIdx, selectedPosition.row);
  }

  function handleCopy() {
    const {
      idx,
      rowIdx
    } = selectedPosition;
    if (onCopy) onCopy({
      selectedPosition: selectedPosition,
      selectedRange: selectedRange
    });
    setCopiedCell({
      row: rawRows[getRawRowIdx(rowIdx)],
      columnKey: columns[idx].key,
      selectedRange: selectedRange.topLeft.idx > -1 ? selectedRange : null
    });
  }

  function handlePaste() {
    if (!onPaste || !onRowsChange || copiedCell === null || !isCellEditable(selectedPosition)) {
      return;
    }

    const {
      idx,
      rowIdx
    } = selectedPosition;
    const targetRow = rawRows[getRawRowIdx(rowIdx)];
    const updatedTargetRow = onPaste({
      sourceRow: copiedCell.row,
      sourceColumnKey: copiedCell.columnKey,
      targetRow,
      targetColumnKey: columns[idx].key,
      selectedRange: copiedCell.selectedRange
    });

    if (updatedTargetRow) {
      updateRow(rowIdx, updatedTargetRow);
    }
  }

  function handleCellInput(event) {
    var _a, _b;

    if (!selectedCellIsWithinViewportBounds) return;
    const row = rows[selectedPosition.rowIdx];
    const {
      key
    } = event; // Select the row on Shift + Space
    // if (isSelectable && shiftKey && key === ' ') {
    //   assertIsValidKeyGetter<R, K>(rowKeyGetter);
    //   const rowKey = rowKeyGetter(row);
    //   selectRow({ row, checked: !selectedRows.has(rowKey), isShiftClick: false });
    //   // do not scroll
    //   event.preventDefault();
    //   return;
    // }

    const column = columns[selectedPosition.idx];
    (_b = (_a = column.editorOptions) === null || _a === void 0 ? void 0 : _a.onCellKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, event);
    if (event.isDefaultPrevented()) return;

    if (isCellEditable(selectedPosition) && isDefaultCellInput(event)) {
      setSelectedPosition(({
        idx,
        rowIdx
      }) => ({
        idx,
        rowIdx,
        mode: 'EDIT',
        row,
        originalRow: row
      }));
    } // TODO: make it extensible
    // Pressing enter key opens the editor but also it injects the enter key
    // into the editor which adds a new line to the editor


    if (key === 'Enter') {
      event.preventDefault();
    }
  }
  /**
   * utils
   */


  function isColIdxWithinSelectionBounds(idx) {
    return idx >= minColIdx && idx <= maxColIdx;
  }

  function isRowIdxWithinViewportBounds(rowIdx) {
    return rowIdx >= 0 && rowIdx < rows.length;
  }

  function isCellWithinSelectionBounds({
    idx,
    rowIdx
  }) {
    return rowIdx >= minRowIdx && rowIdx <= maxRowIdx && isColIdxWithinSelectionBounds(idx);
  }

  function isCellWithinViewportBounds({
    idx,
    rowIdx
  }) {
    return isRowIdxWithinViewportBounds(rowIdx) && isColIdxWithinSelectionBounds(idx);
  }

  function handleEditorRowChange(row, commitChanges, isClose = true) {
    if (selectedPosition.mode === 'SELECT') return;

    if (commitChanges) {
      updateRow(getRawRowIdx(selectedPosition.rowIdx), row);

      if (isClose) {
        closeEditor();
      }
    } else {
      setSelectedPosition(position => Object.assign(Object.assign({}, position), {
        row
      }));
    }
  }

  function isCellEditable(position) {
    return isCellWithinViewportBounds(position) && isSelectedCellEditable({
      columns,
      rows,
      selectedPosition: position
    });
  }

  function selectCell(position, enableEditor) {
    if (!isCellWithinSelectionBounds(position)) return;
    commitEditorChanges();

    if (enableEditor && isCellEditable(position)) {
      const row = rows[position.rowIdx];
      setSelectedPosition(Object.assign(Object.assign({}, position), {
        mode: 'EDIT',
        row,
        originalRow: row
      }));
      setSelectedRange(initialRange);
    } else if (isSamePosition(selectedPosition, position)) {
      // Avoid re-renders if the selected cell state is the same
      // TODO: replace with a #record? https://github.com/microsoft/TypeScript/issues/39831
      scrollToCell(position);
    } else {
      setSelectedPosition(Object.assign(Object.assign({}, position), {
        mode: 'SELECT'
      }));
      setSelectedRange(initialRange);
    }
  }

  function closeEditor() {
    if (selectedPosition.mode === 'SELECT') return;
    setSelectedPosition(({
      idx,
      rowIdx
    }) => ({
      idx,
      rowIdx,
      mode: 'SELECT'
    }));
    setSelectedRange(initialRange);
  }

  function scrollToCell({
    idx,
    rowIdx
  }) {
    const {
      current
    } = gridRef;
    if (!current) return;

    if (typeof idx === 'number' && idx > lastFrozenColumnIndex) {
      rowIdx !== null && rowIdx !== void 0 ? rowIdx : rowIdx = selectedPosition.rowIdx;
      if (!isCellWithinSelectionBounds({
        rowIdx,
        idx
      })) return;
      const {
        clientWidth
      } = current;
      const column = columns[idx];
      const {
        left,
        width
      } = columnMetrics.get(column);
      let right = left + width;
      const colSpan = getSelectedCellColSpan({
        rows,
        summaryRows,
        rowIdx,
        lastFrozenColumnIndex,
        column
      });

      if (colSpan !== undefined) {
        const {
          left,
          width
        } = columnMetrics.get(columns[column.idx + colSpan - 1]);
        right = left + width;
      }

      const isCellAtLeftBoundary = left < scrollLeft + totalFrozenColumnWidth;
      const isCellAtRightBoundary = right > clientWidth + scrollLeft;

      if (isCellAtLeftBoundary) {
        current.scrollLeft = left - totalFrozenColumnWidth;
      } else if (isCellAtRightBoundary) {
        current.scrollLeft = right - clientWidth;
      }
    }

    if (typeof rowIdx === 'number' && isRowIdxWithinViewportBounds(rowIdx)) {
      const rowTop = getRowTop(rowIdx);
      const rowHeight = getRowHeight(rowIdx);

      if (rowTop < scrollTop) {
        // at top boundary, scroll to the row's top
        current.scrollTop = rowTop;
      } else if (typeof rowHeight === 'number' && rowTop + rowHeight > scrollTop + clientHeight) {
        // at bottom boundary, scroll the next row's top to the bottom of the viewport
        current.scrollTop = rowTop + rowHeight - clientHeight;
      }
    }
  }

  function getNextPosition(key, ctrlKey, shiftKey, currentPosition) {
    const {
      idx,
      rowIdx
    } = currentPosition;
    const isRowSelected = selectedCellIsWithinSelectionBounds && idx === -1;

    switch (key) {
      case 'ArrowUp':
        return {
          idx,
          rowIdx: rowIdx - 1
        };

      case 'ArrowDown':
        return {
          idx,
          rowIdx: rowIdx + 1
        };

      case 'ArrowLeft':
        return {
          idx: idx - 1,
          rowIdx
        };

      case 'ArrowRight':
        return {
          idx: idx + 1,
          rowIdx
        };

      case 'Tab':
        return {
          idx: idx + (shiftKey ? -1 : 1),
          rowIdx
        };

      case 'Home':
        // If row is selected then move focus to the first row
        if (isRowSelected) return {
          idx,
          rowIdx: 0
        };
        return {
          idx: 0,
          rowIdx: ctrlKey ? minRowIdx : rowIdx
        };

      case 'End':
        // If row is selected then move focus to the last row.
        if (isRowSelected) return {
          idx,
          rowIdx: rows.length - 1
        };
        return {
          idx: maxColIdx,
          rowIdx: ctrlKey ? maxRowIdx : rowIdx
        };

      case 'PageUp':
        {
          if (currentPosition.rowIdx === minRowIdx) return currentPosition;
          const rowHeight = getRowHeight(rowIdx);
          const nextRowY = getRowTop(rowIdx) + (typeof rowHeight === 'number' ? rowHeight : 0) - clientHeight;
          return {
            idx,
            rowIdx: nextRowY > 0 ? findRowIdx(nextRowY) : 0
          };
        }

      case 'PageDown':
        {
          if (currentPosition.rowIdx >= rows.length) return currentPosition;
          const nextRowY = getRowTop(rowIdx) + clientHeight;
          return {
            idx,
            rowIdx: nextRowY < totalRowHeight ? findRowIdx(nextRowY) : rows.length - 1
          };
        }

      default:
        return currentPosition;
    }
  }

  function navigate(event) {
    const {
      key,
      shiftKey
    } = event;
    let mode = cellNavigationMode;

    if (key === 'Tab') {
      if (canExitGrid({
        shiftKey,
        cellNavigationMode,
        maxColIdx,
        minRowIdx,
        maxRowIdx,
        selectedPosition
      })) {
        commitEditorChanges(); // Allow focus to leave the grid so the next control in the tab order can be focused

        return;
      }

      mode = cellNavigationMode === 'NONE' ? 'CHANGE_ROW' : cellNavigationMode;
    } // Do not allow focus to leave


    event.preventDefault();
    const ctrlKey = isCtrlKeyHeldDown(event);
    const nextPosition = getNextPosition(key, ctrlKey, shiftKey, selectedPosition);
    if (isSamePosition(selectedPosition, nextPosition)) return;
    const nextSelectedCellPosition = getNextSelectedCellPosition({
      columns,
      colSpanColumns,
      rows,
      summaryRows,
      minRowIdx,
      maxRowIdx,
      lastFrozenColumnIndex,
      cellNavigationMode: mode,
      currentPosition: selectedPosition,
      nextPosition,
      isCellWithinBounds: isCellWithinSelectionBounds
    });
    selectCell(nextSelectedCellPosition);
  }

  function getDraggedOverCellIdx(currentRowIdx) {
    if (draggedOverRowIdx === undefined) return;
    const {
      rowIdx
    } = selectedPosition;
    const isDraggedOver = rowIdx < draggedOverRowIdx ? rowIdx < currentRowIdx && currentRowIdx <= draggedOverRowIdx : rowIdx > currentRowIdx && currentRowIdx >= draggedOverRowIdx;
    return isDraggedOver ? selectedPosition.idx : undefined;
  }

  function getDragHandle(rowIdx) {
    if (selectedPosition.rowIdx !== rowIdx || selectedPosition.mode === 'EDIT' || onFill == null) {
      return;
    }

    return /*#__PURE__*/React.createElement(DragHandle, {
      rows: rawRows,
      columns: columns,
      selectedPosition: selectedPosition,
      isCellEditable: isCellEditable,
      latestDraggedOverRowIdx: latestDraggedOverRowIdx,
      onRowsChange: onRowsChange,
      onFill: onFill,
      setDragging: setDragging,
      setDraggedOverRowIdx: setDraggedOverRowIdx
    });
  }

  function getCellEditor(rowIdx) {
    if (selectedPosition.rowIdx !== rowIdx || selectedPosition.mode === 'SELECT') return;
    const {
      idx,
      row
    } = selectedPosition;
    const column = columns[idx];
    const colSpan = getColSpan(column, lastFrozenColumnIndex, {
      type: 'ROW',
      row
    });

    const closeEditor = () => {
      setSelectedPosition(({
        idx,
        rowIdx
      }) => ({
        idx,
        rowIdx,
        mode: 'SELECT'
      }));
    };

    const onRowChange = (row, commitChanges) => {
      if (commitChanges) {
        updateRow(selectedPosition.rowIdx, row);
        closeEditor();
      } else {
        setSelectedPosition(position => Object.assign(Object.assign({}, position), {
          row
        }));
      }
    };

    if (rows[selectedPosition.rowIdx] !== selectedPosition.originalRow) {
      // Discard changes if rows are updated from outside
      closeEditor();
    }

    return /*#__PURE__*/React.createElement(EditCell, {
      key: column.key,
      column: column,
      colSpan: colSpan,
      row: row,
      onRowChange: onRowChange,
      closeEditor: closeEditor,
      scrollToCell: () => {
        scrollToCell(selectedPosition);
      }
    });
  }

  function getViewportRows() {
    var _a;

    const rowElements = [];
    const {
      idx: selectedIdx,
      rowIdx: selectedRowIdx
    } = selectedPosition;
    const startRowIdx = selectedCellIsWithinViewportBounds && selectedRowIdx < rowOverscanStartIdx ? rowOverscanStartIdx - 1 : rowOverscanStartIdx;
    const endRowIdx = selectedCellIsWithinViewportBounds && selectedRowIdx > rowOverscanEndIdx ? rowOverscanEndIdx + 1 : rowOverscanEndIdx;

    for (let viewportRowIdx = startRowIdx; viewportRowIdx <= endRowIdx; viewportRowIdx++) {
      const isRowOutsideViewport = viewportRowIdx === rowOverscanStartIdx - 1 || viewportRowIdx === rowOverscanEndIdx + 1;
      const rowIdx = isRowOutsideViewport ? selectedRowIdx : viewportRowIdx;
      let rowColumns = viewportColumns;
      const selectedColumn = columns[selectedIdx]; // selectedIdx can be -1 if grouping is enabled
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition

      if (selectedColumn !== undefined) {
        if (isRowOutsideViewport) {
          // if the row is outside the viewport then only render the selected cell
          rowColumns = [selectedColumn];
        } else if (selectedRowIdx === rowIdx && !viewportColumns.includes(selectedColumn)) {
          // if the row is within the viewport and cell is not, add the selected column to viewport columns
          rowColumns = selectedIdx > viewportColumns[viewportColumns.length - 1].idx ? [...viewportColumns, selectedColumn] : [...viewportColumns.slice(0, lastFrozenColumnIndex + 1), selectedColumn, ...viewportColumns.slice(lastFrozenColumnIndex + 1)];
        }
      }

      const row = rows[rowIdx];
      const top = getRowTop(rowIdx) + headerRowHeight;
      let key;
      let isRowSelected = false;

      if (typeof rowKeyGetter === 'function') {
        key = rowKeyGetter(row);
        isRowSelected = (_a = selectedRows === null || selectedRows === void 0 ? void 0 : selectedRows.has(key)) !== null && _a !== void 0 ? _a : false;
      } else {
        key = rowIdx;
      }

      rowElements.push( /*#__PURE__*/React.createElement(RowRenderer, {
        "aria-rowindex": headerRowsCount + rowIdx + 1,
        "aria-selected": isSelectable ? isRowSelected : undefined,
        key: key,
        rowIdx: rowIdx,
        row: row,
        viewportColumns: rowColumns,
        isRowSelected: isRowSelected,
        onRowClick: onRowClick,
        onRowDoubleClick: onRowDoubleClick,
        rowClass: rowClass,
        top: top,
        height: getRowHeight(rowIdx),
        copiedCellIdx: copiedCell !== null && copiedCell.row === row ? columns.findIndex(c => c.key === copiedCell.columnKey) : undefined,
        selectedCellIdx: selectedRowIdx === rowIdx ? selectedIdx : undefined,
        draggedOverCellIdx: getDraggedOverCellIdx(rowIdx),
        setDraggedOverRowIdx: isDragging ? setDraggedOverRowIdx : undefined,
        lastFrozenColumnIndex: lastFrozenColumnIndex,
        onRowChange: handleFormatterRowChangeLatest,
        selectCell: selectViewportCellLatest,
        selectedCellDragHandle: getDragHandle(rowIdx),
        selectedCellEditor: getCellEditor(rowIdx)
      }));
    }

    return rowElements;
  } // Reset the positions if the current values are no longer valid. This can happen if a column or row is removed


  if (selectedPosition.idx > maxColIdx || selectedPosition.rowIdx > maxRowIdx) {
    setSelectedPosition(initialPosition);
    setDraggedOverRowIdx(undefined);
  }

  return /*#__PURE__*/React.createElement("div", {
    role: 'grid',
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    "aria-multiselectable": isSelectable ? true : undefined,
    "aria-colcount": columns.length,
    "aria-rowcount": headerRowsCount + rowsCount + summaryRowsCount,
    className: clsx(rootClassname, {
      [viewportDraggingClassname]: isDragging
    }, className),
    style: Object.assign(Object.assign(Object.assign({}, style), {
      '--header-row-height': `${headerRowHeight}`,
      '--row-width': `${totalColumnWidth}`,
      '--summary-row-height': `${summaryRowHeight}`
    }), layoutCssVars),
    ref: gridRef,
    onScroll: handleScroll,
    onKeyDown: handleKeyDown,
    "data-gridid": gridId,
    "data-testid": testId
  }, /*#__PURE__*/React.createElement(HeaderRow$1, {
    columns: viewportColumns,
    onColumnResize: handleColumnResize,
    allRowsSelected: allRowsSelected,
    onAllRowsSelectionChange: selectAllRowsLatest,
    sortColumns: sortColumns,
    onSortColumnsChange: onSortColumnsChange,
    lastFrozenColumnIndex: lastFrozenColumnIndex,
    selectedCellIdx: isHeaderRowSelected ? selectedPosition.idx : undefined,
    selectCell: selectHeaderCellLatest,
    shouldFocusGrid: !selectedCellIsWithinSelectionBounds
  }), rows.length === 0 && noRowsFallback ? noRowsFallback : /*#__PURE__*/React.createElement(React.Fragment, null, enableVirtualization && /*#__PURE__*/React.createElement("div", {
    style: {
      height: max(totalRowHeight, clientHeight)
    }
  }), /*#__PURE__*/React.createElement(RowSelectionChangeProvider, {
    value: selectRowLatest
  }, getViewportRows()), summaryRows === null || summaryRows === void 0 ? void 0 : summaryRows.map((row, rowIdx) => {
    const isSummaryRowSelected = selectedPosition.rowIdx === headerRowsCount + rows.length + rowIdx - 1;
    return /*#__PURE__*/React.createElement(SummaryRow$1, {
      "aria-rowindex": headerRowsCount + rowsCount + rowIdx + 1,
      key: rowIdx,
      rowIdx: rowIdx,
      row: row,
      bottom: summaryRowHeight * (summaryRows.length - 1 - rowIdx),
      viewportColumns: viewportColumns,
      lastFrozenColumnIndex: lastFrozenColumnIndex,
      selectedCellIdx: isSummaryRowSelected ? selectedPosition.idx : undefined,
      selectCell: selectSummaryCellLatest
    });
  })), gridRef.current && !selectedRangeIsSingleCell(selectedRange) ? /*#__PURE__*/React.createElement(SelectionRange, Object.assign({}, getSelectedRangeDimensions({
    selectedRange,
    tableElement: gridRef.current
  }))) : null);
}

function isSamePosition(p1, p2) {
  return p1.idx === p2.idx && p1.rowIdx === p2.rowIdx;
}

var DataGrid$1 = /*#__PURE__*/forwardRef(DataGrid);
const textEditorClassname = `rdt-text-editor`;

function autoFocusAndSelect(input) {
  input === null || input === void 0 ? void 0 : input.focus();
  input === null || input === void 0 ? void 0 : input.select();
}

function TextEditor({
  row,
  column,
  onRowChange,
  onClose
}) {
  return /*#__PURE__*/React.createElement("input", {
    className: textEditorClassname,
    ref: autoFocusAndSelect,
    value: row[column.key],
    onChange: event => onRowChange(Object.assign(Object.assign({}, row), {
      [column.key]: event.target.value
    })),
    onBlur: () => onClose(true)
  });
}

export { RowWithRef as Row, SELECT_COLUMN_KEY, SelectCellFormatter, SelectColumn, SortableHeaderCell, TextEditor, ValueFormatter, DataGrid$1 as default, useRowSelection };
//# sourceMappingURL=index.esm.js.map
